import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { Calendar, Clock, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import { DropdownIndicator, ClearIndicator } from '../../components/common/SelectComponents';
import { getSelectStyles } from '../../utils/selectStyles';
import { calculatePlacement } from '../../utils/dropdownPositionUtils';
import { describeCron } from '../../utils/cronUtils';
import './ScheduleJobRun.css';

// ─── Static data ──────────────────────────────────────────────────────────────

export const SCHEDULE_MODES = { ONE_TIME: 'oneTime', CRON: 'cron' };

const RETRY_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }));

const OFFSET_MIN = 15;
const OFFSET_MAX = 300;
const OFFSET_STEP = 15;

const clampOffset = (val) => Math.min(OFFSET_MAX, Math.max(OFFSET_MIN, val));

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

// Selected option styled to match the reference (blue text on light background),
// while inheriting everything else from the shared select styles.
const buildSelectStyles = (hasError) => {
    const base = getSelectStyles(hasError);
    return {
        ...base,
        option: (styleBase, state) => ({
            ...base.option(styleBase, state),
            backgroundColor: state.isSelected ? '#eff6ff' : state.isFocused ? '#f1f5f9' : 'white',
            color: state.isSelected ? '#2563eb' : '#1e293b',
            fontWeight: state.isSelected ? 600 : 400,
        }),
    };
};

// ─── Date helpers (dependency-free; ISO yyyy-mm-dd storage) ─────────────────────

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (iso) => {
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};
const toDisplayDate = (iso) => {
    const d = parseISO(iso);
    return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : '';
};
const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const buildMonthGrid = (viewDate) => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const start = new Date(year, month, 1);
    start.setDate(1 - start.getDay());
    return Array.from({ length: 42 }, (_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return d;
    });
};

// ─── Time helpers (24h "HH:mm" storage) ─────────────────────────────────────────

const toDisplayTime = (hhmm) => {
    if (!hhmm) return '';
    const [h, m] = hhmm.split(':').map(Number);
    const meridiem = h < 12 ? 'AM' : 'PM';
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return `${pad(h12)}:${pad(m)} ${meridiem}`;
};

const parseTime = (hhmm) => {
    if (!hhmm) return { hour: 12, minute: 0, meridiem: 'AM' };
    const [h, m] = hhmm.split(':').map(Number);
    const meridiem = h < 12 ? 'AM' : 'PM';
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return { hour: h12, minute: m, meridiem };
};

const to24h = (hour, minute, meridiem) => {
    let h = hour % 12;
    if (meridiem === 'PM') h += 12;
    return `${pad(h)}:${pad(minute)}`;
};

// ─── Date field ─────────────────────────────────────────────────────────────

const DateField = ({ value, onChange, hasError }) => {
    const [open, setOpen] = useState(false);
    const [viewDate, setViewDate] = useState(() => parseISO(value) || new Date());
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const selected = parseISO(value);
    const today = new Date();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const days = buildMonthGrid(viewDate);
    const display = toDisplayDate(value);

    return (
        <div className="schedule-field" ref={wrapRef}>
            <button
                type="button"
                className={`schedule-trigger ${open ? 'open' : ''} ${hasError ? 'error' : ''}`}
                onClick={() => setOpen((o) => !o)}
            >
                <span className={display ? '' : 'schedule-trigger-placeholder'}>
                    {display || 'yyyy-mm-dd'}
                </span>
                <Calendar className="schedule-trigger-icon" />
            </button>

            {open && (
                <div className="schedule-popover schedule-calendar">
                    <div className="schedule-cal-header">
                        <button
                            type="button"
                            className="schedule-cal-nav"
                            onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
                            aria-label="Previous month"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span className="schedule-cal-title">
                            {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                        </span>
                        <button
                            type="button"
                            className="schedule-cal-nav"
                            onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
                            aria-label="Next month"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                    <div className="schedule-cal-grid">
                        {WEEKDAYS.map((w) => (
                            <div key={w} className="schedule-cal-weekday">{w}</div>
                        ))}
                        {days.map((d) => {
                            const outside = d.getMonth() !== viewDate.getMonth();
                            const isPast = d < startOfToday;
                            const cls = [
                                'schedule-cal-day',
                                outside ? 'outside' : '',
                                isSameDay(d, today) ? 'today' : '',
                                isSameDay(d, selected) ? 'selected' : '',
                            ].filter(Boolean).join(' ');
                            return (
                                <button
                                    key={toISO(d)}
                                    type="button"
                                    className={cls}
                                    disabled={isPast}
                                    onClick={() => { onChange(toISO(d)); setOpen(false); }}
                                >
                                    {d.getDate()}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Time field ─────────────────────────────────────────────────────────────

const TimeField = ({ value, onChange, hasError }) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(() => parseTime(value));
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        setDraft(parseTime(value));
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open, value]);

    const clampHour = (v) => {
        const n = parseInt(v, 10);
        if (Number.isNaN(n)) return 12;
        return Math.min(12, Math.max(1, n));
    };
    const clampMinute = (v) => {
        const n = parseInt(v, 10);
        if (Number.isNaN(n)) return 0;
        return Math.min(59, Math.max(0, n));
    };

    const commit = () => {
        onChange(to24h(draft.hour, draft.minute, draft.meridiem));
        setOpen(false);
    };

    const display = toDisplayTime(value);

    return (
        <div className="schedule-field" ref={wrapRef}>
            <button
                type="button"
                className={`schedule-trigger ${open ? 'open' : ''} ${hasError ? 'error' : ''}`}
                onClick={() => setOpen((o) => !o)}
            >
                <span className={display ? '' : 'schedule-trigger-placeholder'}>
                    {display || 'Select time'}
                </span>
                <Clock className="schedule-trigger-icon" />
            </button>

            {open && (
                <div className="schedule-popover schedule-time">
                    <div className="schedule-time-title">Enter Time</div>
                    <div className="schedule-time-body">
                        <div className="schedule-time-unit">
                            <input
                                type="number"
                                className="schedule-time-input"
                                value={draft.hour}
                                min={1}
                                max={12}
                                onChange={(e) => setDraft((d) => ({ ...d, hour: e.target.value }))}
                                onBlur={(e) => setDraft((d) => ({ ...d, hour: clampHour(e.target.value) }))}
                            />
                            <span className="schedule-time-unit-label">Hour</span>
                        </div>
                        <span className="schedule-time-colon">:</span>
                        <div className="schedule-time-unit">
                            <input
                                type="number"
                                className="schedule-time-input"
                                value={draft.minute}
                                min={0}
                                max={59}
                                onChange={(e) => setDraft((d) => ({ ...d, minute: e.target.value }))}
                                onBlur={(e) => setDraft((d) => ({ ...d, minute: clampMinute(e.target.value) }))}
                            />
                            <span className="schedule-time-unit-label">Minute</span>
                        </div>
                        <div className="schedule-time-meridiem">
                            <button
                                type="button"
                                className={draft.meridiem === 'AM' ? 'active' : ''}
                                onClick={() => setDraft((d) => ({ ...d, meridiem: 'AM' }))}
                            >
                                AM
                            </button>
                            <button
                                type="button"
                                className={draft.meridiem === 'PM' ? 'active' : ''}
                                onClick={() => setDraft((d) => ({ ...d, meridiem: 'PM' }))}
                            >
                                PM
                            </button>
                        </div>
                    </div>
                    <div className="schedule-time-footer">
                        <button type="button" className="schedule-time-btn cancel" onClick={() => setOpen(false)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="schedule-time-btn ok"
                            onClick={() => setDraft((d) => {
                                const next = { ...d, hour: clampHour(d.hour), minute: clampMinute(d.minute) };
                                onChange(to24h(next.hour, next.minute, next.meridiem));
                                setOpen(false);
                                return next;
                            })}
                        >
                            Ok
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Retry Interval (seconds stepper) ────────────────────────────────────────────

// How long a press must be held before it starts auto-repeating, and how often
// it repeats after that.
const OFFSET_REPEAT_DELAY_MS = 400;
const OFFSET_REPEAT_INTERVAL_MS = 80;

const OffsetTimeInput = ({ value, onChange, hasError, disabled }) => {
    // Interval ticks read the latest value from a ref rather than the closed-over
    // prop, since the prop only updates once React re-renders between ticks.
    const valueRef = useRef(value);
    useEffect(() => { valueRef.current = value; }, [value]);

    const handleChange = (e) => {
        // Strip anything that isn't a digit (e.g. pasted "1e2", "+15", "-15") so
        // only plain integers ever reach state.
        const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
        onChange(digitsOnly === '' ? '' : parseInt(digitsOnly, 10));
    };

    const handleBlur = () => {
        const base = value === '' || value == null ? OFFSET_MIN : value;
        onChange(clampOffset(Math.round(base / OFFSET_STEP) * OFFSET_STEP));
    };

    // An empty field resolves to the minimum regardless of direction, matching blur.
    const stepValue = (delta) => {
        const current = valueRef.current;
        const next = (current === '' || current == null) ? OFFSET_MIN : clampOffset(current + delta);
        valueRef.current = next;
        onChange(next);
    };

    // Blocks '+', '-', '.', 'e'/'E' and any other non-digit key at the source
    // (this is now a plain text input, so nothing else restricts them). Arrow
    // Up/Down step the value directly — native number-input arrow stepping no
    // longer applies once the input isn't type="number".
    const handleKeyDown = (e) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.key === 'ArrowUp') { e.preventDefault(); stepValue(OFFSET_STEP); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); stepValue(-OFFSET_STEP); return; }
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
        if (allowedKeys.includes(e.key)) return;
        if (!/^[0-9]$/.test(e.key)) e.preventDefault();
    };

    // Press-and-hold repeat: the button steps once immediately, then keeps
    // stepping on an interval until released.
    const timeoutRef = useRef(null);
    const intervalRef = useRef(null);
    const pointerActiveRef = useRef(false);

    const stopRepeat = () => {
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };
    useEffect(() => stopRepeat, []);

    const startRepeat = (delta) => {
        if (disabled) return;
        pointerActiveRef.current = true;
        stepValue(delta);
        timeoutRef.current = setTimeout(() => {
            intervalRef.current = setInterval(() => stepValue(delta), OFFSET_REPEAT_INTERVAL_MS);
        }, OFFSET_REPEAT_DELAY_MS);
    };

    // Keyboard activation (Enter/Space) fires a click with no preceding
    // mousedown, so it isn't covered by startRepeat — handle it here. Pointer
    // interactions are already stepped via startRepeat, so skip the duplicate.
    const handleClick = (delta) => {
        if (pointerActiveRef.current) {
            pointerActiveRef.current = false;
            return;
        }
        stepValue(delta);
    };

    return (
        <div className={`schedule-offset ${hasError ? 'error' : ''}`}>
            <div className="schedule-offset-input-wrap">
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="schedule-offset-input"
                    value={value}
                    disabled={disabled}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                />
                <div className="schedule-offset-spinner">
                    <button
                        type="button"
                        className="schedule-offset-spin-btn"
                        onMouseDown={() => startRepeat(OFFSET_STEP)}
                        onMouseUp={stopRepeat}
                        onMouseLeave={stopRepeat}
                        onTouchStart={(e) => { e.preventDefault(); startRepeat(OFFSET_STEP); }}
                        onTouchEnd={stopRepeat}
                        onClick={() => handleClick(OFFSET_STEP)}
                        disabled={disabled}
                        aria-label="Increase seconds"
                    >
                        <ChevronUp size={12} />
                    </button>
                    <button
                        type="button"
                        className="schedule-offset-spin-btn"
                        onMouseDown={() => startRepeat(-OFFSET_STEP)}
                        onMouseUp={stopRepeat}
                        onMouseLeave={stopRepeat}
                        onTouchStart={(e) => { e.preventDefault(); startRepeat(-OFFSET_STEP); }}
                        onTouchEnd={stopRepeat}
                        onClick={() => handleClick(-OFFSET_STEP)}
                        disabled={disabled}
                        aria-label="Decrease seconds"
                    >
                        <ChevronDown size={12} />
                    </button>
                </div>
            </div>
            <span className="schedule-offset-label">seconds</span>
            <span className="schedule-offset-hint">({OFFSET_MIN}-{OFFSET_MAX} seconds)</span>
        </div>
    );
};

// ─── Retry + Offset (shared between both tabs) ───────────────────────────────

const RetryOffsetRow = ({ schedule, errors, onChange, modalBodyRef }) => {
    const retryRef = useRef(null);
    const retrySelectRef = useRef(null);
    const [retryPlacement, setRetryPlacement] = useState('bottom');

    return (
        <div className="form-row">
            <div className="form-group">
                <label>Retry Attempts</label>
                <div ref={retryRef}>
                    <Select
                        ref={retrySelectRef}
                        value={RETRY_OPTIONS.find((o) => o.value === schedule.retryAttempts) || null}
                        onChange={(opt, meta) => {
                            onChange('retryAttempts', opt ? opt.value : '');
                            if (meta.action === 'clear') retrySelectRef.current?.blur();
                        }}
                        options={RETRY_OPTIONS}
                        placeholder="Select"
                        isClearable
                        styles={buildSelectStyles(!!errors.scheduleRetry)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        menuPlacement={retryPlacement}
                        onMenuOpen={() => setRetryPlacement(calculatePlacement(retryRef, RETRY_OPTIONS.length))}
                        closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                        components={{ DropdownIndicator, ClearIndicator }}
                    />
                </div>
                <ErrorMessage message={errors.scheduleRetry} />
            </div>
            <div className="form-group">
                <label>Retry Interval</label>
                <OffsetTimeInput
                    value={schedule.offsetTime}
                    onChange={(val) => onChange('offsetTime', val)}
                    hasError={!!errors.scheduleOffset}
                    disabled={!schedule.retryAttempts}
                />
                <ErrorMessage message={errors.scheduleOffset} />
            </div>
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ScheduleJobRun = ({ schedule, errors, onChange, onModeChange, modalBodyRef }) => {
    const cronResult = describeCron(schedule.cronExpression);
    const cronHasInput = !!schedule.cronExpression?.trim();
    const cronError = errors.scheduleCron || (cronHasInput && !cronResult.valid ? cronResult.error : '');

    return (
        <div className="schedule-body">
            {/* Tabs */}
            <div className="schedule-tabs schema-tabs">
                <div className="schema-tab-group">
                    <label className={`schema-tab ${schedule.mode === SCHEDULE_MODES.ONE_TIME ? 'active' : ''}`}>
                        <input
                            type="radio"
                            name="scheduleMode"
                            value={SCHEDULE_MODES.ONE_TIME}
                            checked={schedule.mode === SCHEDULE_MODES.ONE_TIME}
                            onChange={() => onModeChange(SCHEDULE_MODES.ONE_TIME)}
                        />
                        One-time
                    </label>
                    <label className={`schema-tab ${schedule.mode === SCHEDULE_MODES.CRON ? 'active' : ''}`}>
                        <input
                            type="radio"
                            name="scheduleMode"
                            value={SCHEDULE_MODES.CRON}
                            checked={schedule.mode === SCHEDULE_MODES.CRON}
                            onChange={() => onModeChange(SCHEDULE_MODES.CRON)}
                        />
                        Custom CRON
                    </label>
                </div>
            </div>

            {/* One-time tab */}
            {schedule.mode === SCHEDULE_MODES.ONE_TIME && (
                <div className="form-row">
                    <div className="form-group">
                        <label>Date<span className="required">*</span></label>
                        <DateField
                            value={schedule.date}
                            hasError={!!errors.scheduleDate}
                            onChange={(v) => onChange('date', v)}
                        />
                        <ErrorMessage message={errors.scheduleDate} />
                    </div>
                    <div className="form-group">
                        <label>Time<span className="required">*</span></label>
                        <TimeField
                            value={schedule.time}
                            hasError={!!errors.scheduleTime}
                            onChange={(v) => onChange('time', v)}
                        />
                        <ErrorMessage message={errors.scheduleTime} />
                    </div>
                </div>
            )}

            {/* Custom CRON tab */}
            {schedule.mode === SCHEDULE_MODES.CRON && (
                <div className="schedule-cron-row">
                    <div className="form-group schedule-cron-field">
                        <label>
                            CRON Expression<span className="required">*</span>
                            <span className="cron-utc-hint">(All cron expressions are evaluated in UTC.)</span>
                        </label>
                        <input
                            type="text"
                            placeholder="e.g., 30 20 * * 1"
                            value={schedule.cronExpression}
                            onChange={(e) => onChange('cronExpression', e.target.value)}
                            className={cronError ? 'error' : ''}
                        />
                        <ErrorMessage message={cronError} />
                    </div>
                    {cronResult.valid && cronHasInput && (
                        <div className="schedule-cron-desc">{cronResult.text}</div>
                    )}
                </div>
            )}

            {/* Retry + Offset (both tabs) */}
            <RetryOffsetRow
                schedule={schedule}
                errors={errors}
                onChange={onChange}
                modalBodyRef={modalBodyRef}
            />
        </div>
    );
};

export default ScheduleJobRun;